import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";

import { PosMovementsService } from "src/app/shared/services/pos-movements/posMovements.service";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, skip, tap } from "rxjs/operators";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import {
	AppState,
	loadedCustomerPartyListState,
} from "src/app/store/reducers";
import { Store, select } from "@ngrx/store";
import { CountryDTO } from "src/app/models/Country/countryDTO";
import { DraftDocumentOutDTO } from "src/app/models/movements/movements";
import { cloneDeep } from "lodash";
import { Router } from "@angular/router";
import { SearchCustomerDialogComponent } from "../search-customer-dialog/search-customer-dialog.component";

export const FORM_PARAMS = {
	editCustomerName: "editCustomerName",
	editCustomerVatin: "editCustomerVatin",
	editCustomerStreet: "editCustomerStreet",
	editCustomerCity: "editCustomerCity",
	editCustomerPostalZipCode: "editCustomerPostalZipCode",
	editCustomerIsoCountryCode: "editCustomerIsoCountryCode",
	editCustomerLicensePlate: "editCustomerLicensePlate",
};

@Component({
	selector: "app-edit-customer-details-dialog",
	templateUrl: "./edit-customer-details-dialog.component.html",
	styleUrls: ["./edit-customer-details-dialog.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class EditCustomerDetailsComponent implements OnInit, OnDestroy {
	//Movement
	isLoading$ = new BehaviorSubject<boolean>(false);
	loadedDocument$: Observable<any>;

	//Form
	partyDataForm: FormGroup;

	//Customers Search
	isLoadingCustomer$: Observable<boolean>;
	customersList$: Observable<CustomerPartyDTO[]>;
	customersList: any[] = [];
	customersSub: Subscription;
	searchTimer: number;

	public countriesList: CountryDTO[];

	//Subscriptions
	loadDocumentInfoSubscription: Subscription;
	updateDocumentBuilderSaveSubscription: Subscription;

	changesTimer: number;
	currentDocument: any;

	// loaded locale
	public locale: string = localStorage.getItem("locale");

	private defaultFormValues: any;

	public disabledUpdateButton: boolean = true;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: any,
		private dialogRef: MatDialogRef<EditCustomerDetailsComponent>,
		private store: Store<AppState>,
		private fb: FormBuilder,
		private posMovementsService: PosMovementsService,
		private snackbarService: SnackbarService,
		private translate: TranslateService,
		private router: Router,
		private dialog: MatDialog,
	) {
		this.customersList$ = this.store.pipe(select(loadedCustomerPartyListState));
		this.countriesList = this.data.countriesList;
	}

	ngOnInit(): void {
		this.createDocumentForm();
		this.loadDocumentInfo();
		this.setCustomerSearchSubscription();
	}

	subscribeFormChanges(form: FormGroup) {
		form.valueChanges.subscribe((res) => {
			if (JSON.stringify(res) === JSON.stringify(this.defaultFormValues)) {
				this.disabledUpdateButton = true;
			} else {
				this.disabledUpdateButton = false;
			}
		});
	}

	createDocumentForm() {
		this.partyDataForm = this.fb.group({
			[FORM_PARAMS.editCustomerName]: null,
			[FORM_PARAMS.editCustomerVatin]: [null, [Validators.minLength(2)]],
			[FORM_PARAMS.editCustomerStreet]: null,
			[FORM_PARAMS.editCustomerCity]: null,
			[FORM_PARAMS.editCustomerPostalZipCode]: null,
			[FORM_PARAMS.editCustomerIsoCountryCode]: null,
			[FORM_PARAMS.editCustomerLicensePlate]: null,
		});
	}

	setDocumentForm(document: any) {
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerName)
			.setValue(document.party?.name);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerVatin)
			.setValue(document.party?.vatin);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerStreet)
			.setValue(document.party?.street);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerPostalZipCode)
			.setValue(document.party?.postalZipCode);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerCity)
			.setValue(document.party?.city);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerIsoCountryCode)
			.setValue(document.party?.isoCountryCode);
		this.partyDataForm
			.get(FORM_PARAMS.editCustomerLicensePlate)
			.setValue(document.party?.licensePlate);

		this.defaultFormValues = this.partyDataForm.value;
		const vatin = this.partyDataForm.get(FORM_PARAMS.editCustomerVatin)?.value;
		const disableFieldsOnInit = vatin === '999999990';
		Object.keys(this.partyDataForm.controls).forEach(controlName => {
			if (![FORM_PARAMS.editCustomerVatin, FORM_PARAMS.editCustomerName].includes(controlName)) {
				const control = this.partyDataForm.get(controlName);
				if (disableFieldsOnInit) {
					control?.disable({ emitEvent: false });
					control?.setValue(null, { emitEvent: false }); 
				} else {
					control?.enable({ emitEvent: false });
				}
			}
		});

		this.partyDataForm.get(FORM_PARAMS.editCustomerVatin)?.valueChanges.subscribe(newVatin => {
			const disableFields = newVatin === '999999990';
			Object.keys(this.partyDataForm.controls).forEach(controlName => {
				if (![FORM_PARAMS.editCustomerVatin, FORM_PARAMS.editCustomerName].includes(controlName)) {
					const control = this.partyDataForm.get(controlName);
					if (disableFields) {
						control?.disable({ emitEvent: false });
						control?.setValue(null, { emitEvent: false }); 
					} else {
						control?.enable({ emitEvent: false });
					}
				}
			});
		});

		this.subscribeFormChanges(this.partyDataForm);
	}

	loadDocumentInfo() {
		this.isLoading$.next(true);
		this.loadDocumentInfoSubscription = this.posMovementsService
			.getDocumentBuilderOut(this.data.documentToken)
			.pipe(
				catchError((error) => {
					this.snackbarService.show({
						message: this.translate.instant("PosMovements.documentLoadError"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe((document) => {
				this.currentDocument = cloneDeep(document);
				this.setDocumentForm(document);
			});
	}

	getSelectedCountryFlag(field: string) {
		if (!field || !this.countriesList) {
			return;
		}
		const countryFlagCode = this.countriesList.find(({ code }) => code === field)?.alpha2Code;
		return countryFlagCode ? countryFlagCode.toLowerCase() : null;
	}

	saveDocument() {
		const draftDocumentOut = this.setDraftDocumentOut();
		if (!this.validateForm(this.partyDataForm)) {
			return;
		}
		this.updateDocumentBuilderSave(draftDocumentOut);
	}

	validateForm(partyDataForm: FormGroup) {
		if (!partyDataForm.valid) {
			partyDataForm.markAllAsTouched();
			this.snackbarService.show({
				message: this.translate.instant("PosMovements.errorValidatingForm"),
				type: SnackbarType.ERROR,
			});
			return false;
		}
		return true;
	}

	updateDocumentBuilderSave(draftDocumentOut: DraftDocumentOutDTO) {
		this.isLoading$.next(true);
		this.updateDocumentBuilderSaveSubscription = this.posMovementsService
			.updateDocumentBuilderOutSave(draftDocumentOut)
			.pipe(
				catchError((error) => {
					this.snackbarService.show({
						message:
							this.translate.instant(error) ??
							this.translate.instant("PosMovements.documentSaveFail"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe((updatedDocument) => {
				if (updatedDocument) {
					updatedDocument.listOfDocumentsToPrint.forEach((token) =>
						this.printDocument(token)
					);
					this.dialogRef.close();
					this.router.navigate(["/movements-customer-headers"]);
				}
			});
	}

	setDraftDocumentOut() {
		return {
			...this.currentDocument,
			documentToken: null,
			actionType: "CHANGE_PARTY",
			party: {
				vatin: this.partyDataForm.get(FORM_PARAMS.editCustomerVatin).value,
				name: this.partyDataForm.get(FORM_PARAMS.editCustomerName).value,
				street: this.partyDataForm.get(FORM_PARAMS.editCustomerStreet).value,
				postalZipCode: this.partyDataForm.get(
					FORM_PARAMS.editCustomerPostalZipCode
				).value,
				city: this.partyDataForm.get(FORM_PARAMS.editCustomerCity).value,
				isoCountryCode: this.partyDataForm.get(
					FORM_PARAMS.editCustomerIsoCountryCode
				).value,
				licensePlate: this.partyDataForm.get(
					FORM_PARAMS.editCustomerLicensePlate
				).value,
			},
		};
	}

	setCustomerSearchSubscription() {
		this.customersSub = this.customersList$
			.pipe(skip(1))
			.subscribe((customers) => {
				if (customers) {
					this.customersList = customers.map(
						({
							code,
							customerCode,
							name,
							vatin,
							address,
							statusCode,
							statusDescription,
						}) => {
							return {
								code: code,
								displayCode: customerCode ?? "",
								name: name ?? "",
								vatin: vatin ?? "",
								street: address?.street ?? "",
								city: address?.city ?? "",
								postalZipCode: address?.postalZipCode ?? "",
								isoCountryCode: address?.isoCountryCode ?? "",
								statusCode: statusCode ?? "",
								statusDescription: statusDescription ?? "",
							};
						}
					);
				}
			});
	}

	printDocument(token: string) {
		this.isLoading$.next(true);
		this.posMovementsService
			.printDocument(token)
			.pipe(
				tap((response: any) => {
					const pdfSrc = response[0].data;
					const byteArray = new Uint8Array(
						atob(pdfSrc)
							.split("")
							.map((char) => char.charCodeAt(0))
					);
					const blob = new Blob([byteArray], { type: "application/pdf" });
					const url = window.URL.createObjectURL(blob);
					window.open(url, "_blank");
				}),
				catchError((error) => {
					this.snackbarService.show({
						message:
							this.translate.instant(error) ??
							this.translate.instant("Reports.GeneratingReportError"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe();
	}

	public openSearchCustomerDialog(): void {
		const dialogData = {
			width: '1200px',
			maxHeight: '65vh',
			panelClass: 'search-customer-dialog',
			autoFocus: false,
		};

		const dialogRef = this.dialog.open(SearchCustomerDialogComponent, dialogData);

		dialogRef.afterClosed().subscribe((selectedCustomer) => {
			if (selectedCustomer) {
				this.partyDataForm.get(FORM_PARAMS.editCustomerLicensePlate).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerName).setValue(selectedCustomer.name);
				this.partyDataForm.get(FORM_PARAMS.editCustomerVatin).setValue(selectedCustomer.vatin);
				this.partyDataForm.get(FORM_PARAMS.editCustomerStreet).setValue(selectedCustomer.address.street);
				this.partyDataForm.get(FORM_PARAMS.editCustomerCity).setValue(selectedCustomer.address.city);
				this.partyDataForm.get(FORM_PARAMS.editCustomerPostalZipCode).setValue(selectedCustomer.address.postalZipCode);
				this.partyDataForm.get(FORM_PARAMS.editCustomerIsoCountryCode)
					.setValue(
						this.countriesList.find(
							({ alpha3Code }) => alpha3Code === selectedCustomer.address?.isoCountryCode
						)?.alpha3Code
					);
			} else {
				this.partyDataForm.get(FORM_PARAMS.editCustomerName).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerVatin).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerStreet).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerCity).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerPostalZipCode).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerIsoCountryCode).setValue(null);
				this.partyDataForm.get(FORM_PARAMS.editCustomerLicensePlate).setValue(null);
			}
		});
	}

	ngOnDestroy() {
		if (this.customersSub) {
			this.customersSub.unsubscribe();
		}
		if (this.loadDocumentInfoSubscription) {
			this.loadDocumentInfoSubscription.unsubscribe();
		}
		if (this.updateDocumentBuilderSaveSubscription) {
			this.updateDocumentBuilderSaveSubscription.unsubscribe();
		}
	}
}
